// Klargo Design System — Transaction history (/affarshistorik). The list is the
// mockup's open archival ledger (Klargo-Reference/src/pages/TransactionHistoryPage.tsx
// + components/ds/TransactionRows.tsx): transactions grouped by year, newest
// first, as open hairline rows — plate, vehicle, "Sold to / Bought from",
// date, price, chevron. A row click opens the mockup's own separate interface
// (TransactionRecordPage, at /affarshistorik/:id) instead of a side sheet —
// data fetching is unchanged, only where it's shown moved to its own route.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ApiCall from "../../Apicall/ApiCall";
import AuthShell from "../components/ds/AuthShell";
import { RowChevron } from "../components/ds/StatusMark";
import RegistrationPlate from "../components/common/RegistrationPlate";
import { formatDateLocalized, formatSekAmount } from "../lib/format";
import { useLanguage } from "../components/layout/LanguageSwitcher";

const mapVehicleType = (raw) => {
  if (!raw) return "other";
  const s = raw.toLowerCase();
  if (s === "car" || s === "bil") return "car";
  if (s === "mc" || s === "motorcycle") return "mc";
  if (s === "moped") return "moped";
  if (s === "husbil" || s === "camper" || s === "rv") return "camper";
  if (s === "lätt lastbil" || s === "light truck" || s === "lighttruck" || s === "light_truck") return "light_truck";
  return "other";
};

// ═══════════════════════════════════════════════════════════════════════════
// TRANSACTION HISTORY PAGE
// ═══════════════════════════════════════════════════════════════════════════
// ── Archive row — the mockup's ArchiveTransactionRow. One row = one control
// (single tab stop, one accessible name, nothing nested). Reading order never
// changes with width: vehicle → direction + counterparty → date → price.
// ≥920px it is one five-track grid; below it stacks in three fixed lines.
// A non-completed deal's state (In progress / Expired / Cancelled) rides as the
// mockup's secondary note line, so the date and price columns stay clean.
// Hover is a quiet tint painted by a pseudo-element slightly wider than the
// hairlines, and the chevron nudges 2px (.ds-mark-fwd); no other motion.
const REGISTER =
  "min-[920px]:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_124px_92px_8px] min-[920px]:gap-x-4 min-[920px]:py-[13px]";

const ArchiveDealRow = ({ row, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(row.deal)}
    aria-label={row.ariaLabel}
    data-testid="history-entry"
    data-direction={row.direction}
    className={
      "relative isolate grid w-full cursor-pointer grid-cols-[minmax(0,1fr)_8px] items-center gap-x-3.5 border-0 border-b border-solid border-ds-rule-row bg-transparent py-3.5 text-left text-inherit font-normal " +
      "before:pointer-events-none before:absolute before:-inset-x-2.5 before:inset-y-[3px] before:-z-10 before:rounded-[10px] before:bg-[rgba(20,36,46,.045)] before:opacity-0 before:transition-opacity hover:before:opacity-100 " +
      "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ds-green " +
      REGISTER
    }
  >
    <span className="min-w-0 min-[920px]:contents">
      <span data-testid="history-entry-identity" className="flex min-w-0 items-center gap-x-3">
        <span className="flex w-[108px] flex-none items-center">
          {row.reg && <RegistrationPlate value={row.reg} size="compact" twoRow={row.twoRow} />}
        </span>
        <span className="min-w-0">
          <span className="ds-title-row block">{row.name}</span>
          {row.outcome && (
            <span className="mt-[2px] block text-[12.5px] leading-snug text-ds-muted-soft min-[920px]:hidden">{row.outcome}</span>
          )}
        </span>
      </span>

      <span className="mt-[7px] block min-w-0 min-[920px]:contents">
        <span data-testid="history-entry-direction" className="block min-w-0 text-[13.5px] leading-snug">
          <span className="font-medium text-ds-ink">{row.verb}</span>{" "}
          <span className="text-ds-muted">{row.counterparty}</span>
          {row.outcome && (
            <span className="mt-[3px] hidden text-[12.5px] leading-snug text-ds-muted-soft min-[920px]:block">{row.outcome}</span>
          )}
        </span>

        <span className="mt-[5px] flex items-baseline justify-between gap-x-4 min-[920px]:contents">
          <span data-testid="history-entry-date" className="whitespace-nowrap text-[13px] tabular-nums leading-snug text-ds-muted">
            {row.date}
          </span>
          <span
            data-testid="history-entry-amount"
            className="flex flex-none items-baseline justify-end gap-[3px] whitespace-nowrap text-right text-sm font-medium tabular-nums text-ds-text"
          >
            {row.amount}
          </span>
        </span>
      </span>
    </span>
    <RowChevron className="h-[13px] w-2 justify-self-end" />
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════
// TRANSACTION HISTORY PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function TransactionHistoryPage() {
  const { t, i18n } = useTranslation();
  const { t: tp, languageCode } = useLanguage();
  const navigate = useNavigate();

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);

  const STATUS_LABELS = {
    completed: t("history.status.completed"),
    in_progress: t("history.status.inProgress"),
    expired: t("history.status.expired"),
    cancelled: t("history.status.cancelled"),
  };

  // ── Fetch + shape deals ─────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        let uid = currentUserId;
        if (!uid) {
          const sessionRes = await ApiCall({ url: "/Session/GetCurrentLoginInformations", method: "GET" });
          uid = sessionRes?.data?.result?.user?.id;
          if (active) setCurrentUserId(uid);
        }

        const dealsRes = await ApiCall({ url: "/ContractMain/GetMyDeals", method: "GET" });
        const raw = dealsRes?.data?.result || [];
        const mapped = raw.map((item) => {
          const isSeller = item.sellerUserId === uid;
          const vehicleTypeKey = mapVehicleType(item.vahicleTypeOptionVahicleTypeName);
          const regNo = item.carInfoRegistrationNo || "";
          const typeLabel = item.vahicleTypeOptionName || item.vahicleTypeOptionVahicleTypeName || "";
          const namePart = [item.carInfoTradeName || item.carInfoVehicleDesignation || "", item.carInfoYearModel || ""]
            .join(" ")
            .trim();
          const vehicleTitle = namePart || (typeLabel && regNo ? `${typeLabel} · ${regNo}` : "") || regNo || typeLabel || "—";
          const status = item.dealState || (item.dealComplete ? "completed" : "in_progress");
          const completedDate = item.buyerSignedAt || item.sellerSignedAt || null;
          return {
            id: item.id,
            vehicleTitle,
            regNo: regNo || "—",
            vehicleTypeKey,
            vehicleTypeRaw: item.vahicleTypeOptionVahicleTypeName || "",
            userRole: isSeller ? "SELLER" : "BUYER",
            sellerUserId: item.sellerUserId,
            counterpartyName: ((isSeller ? item.buyerName : item.sellerName) || "").trim(),
            status,
            completedDate,
            creationTime: item.creationTime || null,
            effectiveDate: completedDate || item.creationTime || null,
            amountSek: item.carValuationBySeller || 0,
          };
        });

        if (active) setDeals(mapped);
      } catch (err) {
        console.error("Failed to load deals", err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [currentUserId]);

  // Newest first, grouped into one chapter per year (the mockup's ledger).
  const groups = useMemo(() => {
    const ts = (v) => (v ? new Date(v).getTime() : 0);
    const rows = [...deals]
      .sort((a, b) => ts(b.effectiveDate) - ts(a.effectiveDate))
      .map((deal) => {
        const isSell = deal.userRole === "SELLER";
        const date = deal.effectiveDate ? formatDateLocalized(deal.effectiveDate, languageCode) : "—";
        const counterparty = deal.counterpartyName
          ? tp(isSell ? "ds.transaction.counterparty.sold" : "ds.transaction.counterparty.bought").replace(
              "{counterparty}",
              deal.counterpartyName
            )
          : "";
        return {
          id: deal.id,
          deal,
          direction: isSell ? "sell" : "buy",
          name: deal.vehicleTitle,
          verb: tp(isSell ? "ds.transaction.direction.sold" : "ds.transaction.direction.bought"),
          counterparty,
          date,
          amount: deal.amountSek > 0 ? formatSekAmount(deal.amountSek, languageCode) : "—",
          reg: deal.regNo !== "—" ? deal.regNo : "",
          twoRow: deal.vehicleTypeKey === "mc",
          outcome: deal.status === "completed" ? "" : STATUS_LABELS[deal.status] || "",
          year: deal.effectiveDate ? new Date(deal.effectiveDate).getFullYear() : 0,
          ariaLabel: tp(isSell ? "ds.transaction.aria.sale" : "ds.transaction.aria.purchase")
            .replace("{vehicle}", deal.vehicleTitle)
            .replace("{date}", date),
        };
      });
    const byYear = [];
    rows.forEach((row) => {
      let group = byYear.find((g) => g.year === row.year);
      if (!group) {
        group = { year: row.year, label: row.year ? String(row.year) : "—", rows: [] };
        byYear.push(group);
      }
      group.rows.push(row);
    });
    return byYear.sort((a, b) => b.year - a.year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, languageCode, i18n.language]);

  // Row click opens the mockup's own separate interface instead of a side
  // sheet.
  const handleRowClick = (deal) => {
    navigate(`/affarshistorik/${deal.id}`);
  };

  const countText = tp(deals.length === 1 ? "ds.transaction.count.one" : "ds.transaction.count.many").replace(
    "{count}",
    String(deals.length)
  );

  return (
    <AuthShell t={t} activeKey="history">
      <section className="ds-animate-page ds-auth-rail max-w-[880px] pb-[var(--ds-page-bottom)] pt-[var(--ds-page-top)]">
        <h1 className="ds-title-route m-0 mb-1.5">{t("history.title")}</h1>
        <div className="ds-label">{loading ? "" : deals.length === 0 ? tp("ds.history.meta.empty") : countText}</div>

        {loading ? (
          <div className="mt-[34px] flex justify-center py-16" role="status" aria-label={t("history.title")}>
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-ds-rule-edge"
              style={{ borderTopColor: "var(--ds-green)" }}
            />
          </div>
        ) : deals.length === 0 ? (
          <div className="mt-[34px] max-w-[520px] rounded-[14px] border border-ds-sheet-edge bg-ds-sheet p-[clamp(28px,4vw,40px)]">
            <h2 className="ds-title-chapter m-0 mb-2">{tp("ds.history.empty.title")}</h2>
            <p className="ds-body m-0 mb-5">{tp("ds.history.empty.body")}</p>
            <button type="button" onClick={() => navigate("/user/ChooseAction")} className="ds-action-ink">
              {tp("ds.registry.empty.cta")}
            </button>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.year} data-testid="history-year" aria-labelledby={`history-year-${group.year}`} className="mt-8">
              <h2
                id={`history-year-${group.year}`}
                className="ds-serif m-0 border-b border-[rgba(20,36,46,.25)] pb-2 text-[17px] font-semibold leading-tight text-ds-ink"
              >
                {group.label}
              </h2>
              <div>
                {group.rows.map((row) => (
                  <ArchiveDealRow key={row.id} row={row} onOpen={handleRowClick} />
                ))}
              </div>
            </section>
          ))
        )}
      </section>
    </AuthShell>
  );
}
